import { z } from 'zod';
import { camelCaseToTitleCase } from '../../promotions/view-model/utils';
import { ChallengeForm } from './ChallengeForm.schema';
import { toSetError } from '../../promotions/view-model/zod-utils';
import { F2 } from '@client/new/helpers/types';
import { createDate, DateTime } from './utils';
import { LiteChallenge } from '../model/Challenges.types';

type DateTimeGroup = Array<keyof DateTime>;

const isDateTimestampField = (field: unknown): field is DateTime =>
  field !== null && typeof field === 'object' && 'dateTimestamp' in field;

const validateDateTimeField =
  <T extends keyof ChallengeForm>(handler: F2<T, any, void>) =>
  (p: { field: T; withSubfields: DateTimeGroup }) => {
    return handler(p.field, {
      conditions: (data: Partial<ChallengeForm>) => {
        const field = data[p.field];
        if (isDateTimestampField(field))
          return p.withSubfields.some(
            (subKey) => field[subKey] === null || field[subKey] === '',
          );
        return true;
      },
      error: () => `${camelCaseToTitleCase(p.field)} is required`,
      path: [p.field, p.withSubfields[0]],
    });
  };

function rangesOverlap(start1: Date, end1: Date, start2: Date, end2: Date) {
  return start1 < end2 && start2 < end1;
}

export const validateChallengeForm =
  (model: LiteChallenge[], currentId?: string) =>
  (data: Partial<ChallengeForm>, ctx: z.RefinementCtx): void => {
    const validate = toSetError(data, ctx);
    const required = validateDateTimeField(validate);

    const hourFields: DateTimeGroup = ['dateHour', 'dateMinute', 'dateDayTime'];

    validate('name');
    required({ field: 'registrationStart', withSubfields: hourFields });
    required({ field: 'registrationEnd', withSubfields: hourFields });
    required({ field: 'completionStart', withSubfields: hourFields });
    required({ field: 'completionEnd', withSubfields: hourFields });

    required({ field: 'registrationEnd', withSubfields: ['dateTimestamp'] });
    required({ field: 'completionEnd', withSubfields: ['dateTimestamp'] });
    required({ field: 'completionStart', withSubfields: ['dateTimestamp'] });
    required({ field: 'registrationStart', withSubfields: ['dateTimestamp'] });

    validate('registrationEnd', {
      conditions: (d) =>
        createDate(d.registrationStart as DateTime) >=
        createDate(d.registrationEnd as DateTime),
      error: () => 'Registration end must be after registration start',
      path: ['registrationEnd', 'dateTimestamp'],
    });

    validate('completionEnd', {
      conditions: (d) =>
        createDate(d.completionStart as DateTime) >=
        createDate(d.completionEnd as DateTime),
      error: () => 'Completion end must be after completion start',
      path: ['completionEnd', 'dateTimestamp'],
    });

    validate('completionStart', {
      conditions: (d) =>
        createDate(d.registrationEnd as DateTime) >=
        createDate(d.completionStart as DateTime),
      path: ['completionStart', 'dateTimestamp'],
      error: () => 'Completion start must be after registration end',
    });

    validate('registrationStart', {
      conditions: (d) => {
        if (
          !d.registrationStart ||
          !d.registrationEnd ||
          !d.completionStart ||
          !d.completionEnd
        )
          return false;

        const newRegStart = createDate(d.registrationStart as DateTime);
        const newCompEnd = createDate(d.completionEnd as DateTime);

        return model
          .filter((m) => m.challengeId !== currentId)
          .some((existingChallenge) => {
            const [existingRegStart] = existingChallenge.registration.data;
            const [, existingCompEnd] = existingChallenge.completion.data;

            return rangesOverlap(
              newRegStart,
              newCompEnd,
              existingRegStart,
              existingCompEnd,
            );
          });
      },
      error: () =>
        'This challenge overlaps with an existing challenge timeframe.',
      path: ['registrationStart', 'dateTimestamp'],
    });
  };
